import { categoriaDe, idRaiz } from './catalogo'
import type { MapaEstados } from './estadoPecas'
import type { PerfilVeiculo } from './perfilVeiculo'
import type { EtapaAnatomia } from './roteiroAnatomia'

export function pecaPresenteNoPerfil(id: string, perfil: PerfilVeiculo): boolean {
  if (id === 'carterMotor') return perfil.energia.propulsao !== 'eletrico'
  if (id === 'prateleiraTraseira' || id === 'divisoriaPortaMalas') return perfil.volumeTraseiro === 'sedan'
  if (id in perfil.srs) return perfil.srs[id as keyof typeof perfil.srs]
  if (id.startsWith('geradorAirbag')) {
    const chave = `a${id.slice('geradorA'.length)}`
    return chave in perfil.srs && perfil.srs[chave as keyof typeof perfil.srs]
  }
  // `pretensionadorFecho` antes de `pretensionador`: o prefixo do segundo engole o primeiro
  const retencao = (prefixo: string) => {
    const sufixo = id.slice(prefixo.length)
    return perfil.retencao[sufixo.charAt(0).toLowerCase() + sufixo.slice(1)]
  }
  if (id.startsWith('pretensionadorFecho')) return Boolean(retencao('pretensionadorFecho')?.pretensionadorFecho)
  if (id.startsWith('pretensionador')) return Boolean(retencao('pretensionador')?.pretensionador)
  if (id.startsWith('retrator')) return retencao('retrator')?.tipo === 'tresPontos'
  if (['cilindroGnv', 'tubulacaoGnv'].includes(id)) return perfil.energia.cilindroGnv
  if (['bateriaAltaTensao', 'cabeamentoAltaTensao'].includes(id)) return perfil.energia.bateriaAltaTensao
  if (['tanqueCombustivel', 'linhaCombustivel'].includes(id)) return perfil.energia.tanqueCombustivel
  if (['cardan', 'diferencialTraseiro'].includes(id)) return perfil.tracao !== 'dianteira'
  return true
}

export function estadosDaEtapaAnatomia(etapa: EtapaAnatomia): MapaEstados {
  switch (etapa) {
    case 'porta': return { 'portaDianteiraEsquerda.chapaExterna': 'afastada', 'portaDianteiraEsquerda.forro': 'afastada' }
    case 'carroceriaFechamentos': return { capo: 'aberta', tampaTraseira: 'aberta' }
    case 'interior': return { teto: 'removida' }
    case 'srs': return { teto: 'removida', painel: 'transparente', consoleCentral: 'transparente' }
    case 'mecanica': return { capo: 'aberta' }
    case 'rodagem': return { capo: 'aberta', rodas: 'removida' }
    case 'energiaVidros': return { capo: 'aberta', tampaTraseira: 'aberta', piso: 'transparente' }
    default: return {}
  }
}

export function prepararPecaAnatomia(etapa: EtapaAnatomia, id: string) {
  const categoria = categoriaDe(id)
  const modo = categoria === 'habitaculo' ? 'interior'
    : categoria === 'srs' || categoria === 'retencao' ? 'srs'
    : categoria === 'mecanica' ? 'mecanica'
    : categoria === 'energia' ? 'energia'
    : categoria === 'estrutura' || categoria === 'zonaDeformacao' ? 'estrutura' : 'externo'
  const estados = { ...estadosDaEtapaAnatomia(etapa) }
  // O acesso pela lista restaura os ancestrais: uma peça oculta não pode ser enquadrada.
  const partes = id.split('.')
  partes.forEach((_, i) => { estados[partes.slice(0, i + 1).join('.')] = 'normal' })
  if (idRaiz(id) === 'motor' || categoria === 'mecanica' || categoria === 'energia') estados.capo = 'aberta'
  if (categoria === 'habitaculo' || categoria === 'srs' || categoria === 'retencao') estados.teto = 'removida'
  return { modo, estados } as const
}

export const FONTES_ANATOMIA = [
  { nome: 'Euro NCAP — ficha de salvamento e Euro Rescue', url: 'https://www.euroncap.com/press-media/euro-ncap-improves-tertiary-safety-by-introducing-a-mobile-app-for-first-responders-in-europe/' },
  { nome: 'Euro NCAP — glossário de sistemas de segurança', url: 'https://www.euroncap.com/glossary/' },
  { nome: 'Tesla — guias de resposta a emergências por modelo (exemplo de fabricante)', url: 'https://www.tesla.com/firstresponders' },
] as const
